import { 
  Heart, Stethoscope, Pill, Activity, Shield, Zap, Target, 
  TrendingUp, Users, Globe, Settings, Database, Monitor, Brain,
  BarChart3, CheckCircle, Clock, Lock
} from 'lucide-react';
import { ProductConfigs } from './types';

export const healthcareSolutionsConfigs: ProductConfigs = {
  'medical-device-management': {
    keyBenefits: [
      {
        icon: Monitor,
        title: 'Intelligent Device Monitoring',
        description: 'Real-time monitoring of medical equipment with predictive maintenance and automated compliance tracking',
        metrics: '99.9% device uptime',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Maintenance Analytics',
        description: 'AI-powered maintenance scheduling preventing device failures and optimising equipment lifecycle management',
        metrics: '80% maintenance efficiency',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: Target,
        title: 'Automated Calibration System',
        description: 'Automated device calibration with precision tracking and regulatory compliance for medical accuracy',
        metrics: '100% calibration compliance',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Shield,
        title: 'MHRA & CE Compliance Tracking',
        description: 'Comprehensive medical device regulatory compliance with automated MHRA notifications and CE marking management',
        metrics: '100% regulatory compliance',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Equipment Performance Analytics',
        description: 'Advanced analytics dashboard showing device utilisation, maintenance costs, and performance benchmarking',
        metrics: 'Complete performance visibility',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Automated PAT Testing',
        description: 'Integrated portable appliance testing with automated scheduling and compliance certificate generation',
        metrics: 'Zero PAT testing delays',
        color: 'from-cyan-500 to-cyan-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Shield,
        title: 'Medical Device Security',
        description: 'Advanced cybersecurity for medical devices with network isolation and threat detection systems',
        metrics: 'Medical-grade security',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: Database,
        title: 'Asset Lifecycle Management',
        description: 'Comprehensive device lifecycle tracking from procurement to disposal with cost optimisation analytics',
        metrics: 'Complete asset visibility',
        color: 'from-indigo-500 to-indigo-600'
      },
      {
        icon: Globe,
        title: 'Regulatory Compliance Engine',
        description: 'Automated compliance with medical device regulations including FDA, CE marking, and quality standards',
        metrics: 'Global compliance ready',
        color: 'from-cyan-500 to-cyan-600'
      },
      {
        icon: Brain,
        title: 'AI-Powered Device Intelligence',
        description: 'Machine learning algorithms for predictive failure analysis and intelligent maintenance scheduling',
        metrics: 'AI-driven insights',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Activity,
        title: 'IoT Device Integration',
        description: 'Advanced IoT connectivity for real-time device monitoring and automated data collection',
        metrics: 'Smart device connectivity',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Workflow Automation Engine',
        description: 'Intelligent workflow automation for maintenance requests, approvals, and documentation processes',
        metrics: 'Automated workflow efficiency',
        color: 'from-yellow-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Excellence Framework',
        description: 'Automated compliance monitoring for SOC 2, ISO 27001, GDPR, and industry-specific regulations',
        metrics: 'Complete regulatory compliance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Advanced security metrics with threat landscape analysis and security posture optimization',
        metrics: 'Optimized security posture',
        color: 'from-yellow-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Scalable Security Infrastructure',
        description: 'Enterprise-grade security platform supporting unlimited users with global threat intelligence',
        metrics: 'Unlimited security scale',
        color: 'from-emerald-500 to-green-600'
      },
      {
        icon: Zap,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Next-generation firewall with AI-powered DDoS mitigation and intrusion prevention systems',
        metrics: '99.99% attack mitigation',
        color: 'from-red-500 to-pink-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Factor Authentication Suite',
        description: 'Enterprise SSO with biometric authentication, hardware tokens, and risk-based access controls',
        metrics: 'Zero-trust access control',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Security Information & Event Management',
        description: 'Real-time SIEM with behavioral analytics, threat hunting, and automated incident correlation',
        metrics: 'Real-time threat visibility',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Lock,
        title: 'Data Loss Prevention & Backup',
        description: 'Automated data classification, leakage prevention, and secure backup with point-in-time recovery',
        metrics: 'Zero data loss guarantee',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmentation with network traffic analysis and anomaly detection for lateral movement prevention',
        metrics: 'Complete network visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: Clock,
        title: 'Continuous Security Assessment',
        description: 'Automated vulnerability scanning, security testing, and continuous compliance validation',
        metrics: 'Proactive security validation',
        color: 'from-cyan-500 to-blue-600'
      }
    ]
  },

  'patient-relationship-management': {
    keyBenefits: [
      {
        icon: Users,
        title: 'Comprehensive Patient Portals',
        description: 'Secure patient portals with appointment scheduling, medical records access, and direct messaging with healthcare providers',
        metrics: '95% patient engagement',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Heart,
        title: 'Automated Care Coordination',
        description: 'Intelligent care coordination with automated referral management and multi-provider communication',
        metrics: '90% care coordination efficiency',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: TrendingUp,
        title: 'Health Outcome Tracking',
        description: 'Advanced patient outcome monitoring with personalised health dashboards and progress tracking',
        metrics: '85% outcome improvement',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: CheckCircle,
        title: 'Automated Appointment Reminders',
        description: 'Multi-channel appointment reminders via SMS, email, and app notifications to reduce no-shows',
        metrics: '75% no-show reduction',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: BarChart3,
        title: 'Patient Engagement Analytics',
        description: 'Comprehensive analytics on patient engagement patterns, satisfaction scores, and communication effectiveness',
        metrics: 'Complete engagement insights',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Globe,
        title: 'Multi-Location Patient Management',
        description: 'Unified patient management across multiple healthcare facilities with seamless data synchronisation',
        metrics: 'Seamless multi-location care',
        color: 'from-cyan-500 to-cyan-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI-Powered Patient Insights',
        description: 'Machine learning algorithms analysing patient behaviour patterns to predict health risks and engagement opportunities',
        metrics: 'Predictive patient insights',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Activity,
        title: 'Telehealth Integration Platform',
        description: 'Seamless integration with telehealth platforms for virtual consultations and remote patient monitoring',
        metrics: 'Complete telehealth integration',
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Care Gap Identification',
        description: 'Intelligent care gap analysis identifying missed screenings, overdue appointments, and preventive care opportunities',
        metrics: 'Proactive care management',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: Database,
        title: 'Population Health Management',
        description: 'Advanced population health analytics with risk stratification and targeted intervention programs',
        metrics: 'Population health optimization',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: Shield,
        title: 'Patient Journey Optimization',
        description: 'Comprehensive patient journey mapping with automated touchpoint optimization and experience enhancement',
        metrics: 'Optimized patient experience',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Health Alerts',
        description: 'Intelligent health monitoring with automated alerts for critical values and emergency intervention triggers',
        metrics: 'Real-time health monitoring',
        color: 'from-yellow-500 to-yellow-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'HIPAA Compliance Excellence',
        description: 'Complete HIPAA compliance with encrypted patient communications, secure data storage, and audit trail management',
        metrics: 'HIPAA-compliant security',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Patient Data Sync',
        description: 'Instant patient data synchronisation across all touchpoints with real-time updates and notifications',
        metrics: 'Real-time data synchronisation',
        color: 'from-yellow-500 to-yellow-600'
      },
      {
        icon: Settings,
        title: 'Scalable Patient Platform',
        description: 'Enterprise patient management infrastructure supporting unlimited patients with global accessibility',
        metrics: 'Unlimited patient capacity',
        color: 'from-emerald-500 to-emerald-600'
      },
      {
        icon: Lock,
        title: 'Advanced Access Controls',
        description: 'Role-based access controls with multi-factor authentication and granular permission management',
        metrics: 'Secure access management',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Database,
        title: 'Secure Data Integration',
        description: 'FHIR-compliant data integration with secure API endpoints and encrypted data transmission',
        metrics: 'Secure healthcare interoperability',
        color: 'from-indigo-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Performance Monitoring Suite',
        description: 'Comprehensive performance monitoring with system health dashboards and automated scaling',
        metrics: '99.9% system availability',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: CheckCircle,
        title: 'Compliance Audit Management',
        description: 'Automated compliance reporting with audit trail generation and regulatory requirement tracking',
        metrics: 'Audit-ready compliance',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Multi-Tenant Architecture',
        description: 'Secure multi-tenant platform with data isolation and customizable branding for healthcare organisations',
        metrics: 'Enterprise multi-tenancy',
        color: 'from-cyan-500 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Advanced Analytics Engine',
        description: 'Real-time analytics processing with machine learning insights and predictive healthcare modelling',
        metrics: 'Advanced healthcare analytics',
        color: 'from-blue-500 to-blue-600'
      }
    ]
  },

  'fitness-wellness-platform': {
    keyBenefits: [
      {
        icon: Activity,
        title: 'Personalised Fitness Tracking',
        description: 'AI-powered personalised fitness programs with adaptive workout plans and progress monitoring',
        metrics: '90% fitness goal achievement',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Heart,
        title: 'Comprehensive Wellness Coaching',
        description: 'Expert wellness coaching with personalised nutrition plans, mental health support, and lifestyle guidance',
        metrics: '85% wellness improvement',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Users,
        title: 'Community Engagement Platform',
        description: 'Social fitness communities with challenges, leaderboards, and peer support for motivation',
        metrics: '95% user engagement',
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Monitor,
        title: 'Wearable Device Integration',
        description: 'Seamless integration with fitness trackers, smartwatches, and health monitoring devices',
        metrics: 'Universal device compatibility',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Advanced Progress Analytics',
        description: 'Comprehensive progress tracking with detailed analytics, insights, and achievement recognition',
        metrics: 'Complete fitness visibility',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: Target,
        title: 'Gamified Wellness Experience',
        description: 'Engaging gamification elements with rewards, badges, and milestone celebrations to maintain motivation',
        metrics: '80% long-term adherence',
        color: 'from-cyan-500 to-cyan-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Fitness Recommendations',
        description: 'Machine learning algorithms providing personalised workout recommendations based on performance data and goals',
        metrics: 'Intelligent fitness guidance',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Shield,
        title: 'Biometric Health Monitoring',
        description: 'Advanced biometric monitoring with heart rate variability, sleep quality, and stress level analysis',
        metrics: 'Comprehensive health insights',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Virtual Personal Training',
        description: 'AI-powered virtual personal training with real-time form correction and adaptive difficulty adjustment',
        metrics: 'Personalised training experience',
        color: 'from-yellow-500 to-yellow-600'
      },
      {
        icon: Activity,
        title: 'Wellness Intervention Alerts',
        description: 'Intelligent health alerts for unusual patterns, potential injuries, or wellness intervention opportunities',
        metrics: 'Proactive health management',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: Globe,
        title: 'Corporate Wellness Programs',
        description: 'Enterprise wellness solutions with team challenges, corporate health metrics, and employee engagement tools',
        metrics: 'Enterprise wellness management',
        color: 'from-cyan-500 to-cyan-600'
      },
      {
        icon: Database,
        title: 'Health Risk Assessment Engine',
        description: 'Comprehensive health risk analysis with preventive care recommendations and lifestyle modification suggestions',
        metrics: 'Predictive health insights',
        color: 'from-orange-500 to-orange-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Health Data Protection',
        description: 'End-to-end encryption for health and fitness data with secure cloud storage and privacy controls',
        metrics: 'Medical-grade data security',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Fitness Analytics',
        description: 'Instant fitness data processing with real-time performance dashboards and live workout tracking',
        metrics: 'Real-time fitness insights',
        color: 'from-yellow-500 to-yellow-600'
      },
      {
        icon: Settings,
        title: 'Scalable Wellness Infrastructure',
        description: 'Cloud-native wellness platform supporting unlimited users with global accessibility and performance',
        metrics: 'Unlimited user capacity',
        color: 'from-emerald-500 to-emerald-600'
      },
      {
        icon: Lock,
        title: 'Privacy-First Architecture',
        description: 'Privacy-by-design architecture with granular data controls and user consent management',
        metrics: 'Complete privacy control',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Database,
        title: 'Secure Wearable Integration',
        description: 'Secure API integration with wearable devices ensuring encrypted data transmission and storage',
        metrics: 'Secure device connectivity',
        color: 'from-indigo-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Performance Optimisation Engine',
        description: 'Advanced performance monitoring with automated scaling and optimisation for peak user experience',
        metrics: '99.9% platform availability',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: CheckCircle,
        title: 'GDPR Compliance Framework',
        description: 'Complete GDPR compliance with automated data retention policies and user data rights management',
        metrics: 'Full GDPR compliance',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Multi-Region Data Management',
        description: 'Global data residency compliance with regional data centres and localised privacy regulations',
        metrics: 'Global compliance ready',
        color: 'from-cyan-500 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Advanced Analytics Platform',
        description: 'Enterprise analytics infrastructure with machine learning insights and predictive wellness modelling',
        metrics: 'Advanced wellness analytics',
        color: 'from-blue-500 to-blue-600'
      }
    ]
  },

  'dental-practice-management': {
    keyBenefits: [
      {
        icon: Target,
        title: 'Intelligent Appointment Scheduling',
        description: 'AI-powered appointment scheduling with treatment time optimization and automated patient confirmations',
        metrics: '95% scheduling efficiency',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: Monitor,
        title: 'Digital Treatment Planning',
        description: 'Advanced digital treatment planning with 3D imaging integration and treatment progress tracking',
        metrics: '90% treatment accuracy',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: Database,
        title: 'Comprehensive Patient Records',
        description: 'Complete digital patient records with treatment history, imaging, and automated clinical documentation',
        metrics: '100% paperless records',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Practice Performance Analytics',
        description: 'Advanced practice analytics with revenue tracking, patient retention metrics, and operational insights',
        metrics: 'Complete practice visibility',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Automated Insurance Processing',
        description: 'Intelligent insurance claim processing with real-time verification and automated billing workflows',
        metrics: '85% claim approval rate',
        color: 'from-cyan-500 to-cyan-600'
      },
      {
        icon: Users,
        title: 'Patient Communication Hub',
        description: 'Multi-channel patient communication with appointment reminders, treatment updates, and educational content',
        metrics: '90% patient satisfaction',
        color: 'from-pink-500 to-rose-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'AI Diagnostic Assistance',
        description: 'Machine learning-powered diagnostic support with pattern recognition in dental imaging and treatment recommendations',
        metrics: 'AI-enhanced diagnostics',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: Activity,
        title: '3D Treatment Visualisation',
        description: 'Advanced 3D treatment visualisation with virtual treatment simulation and patient education tools',
        metrics: 'Immersive treatment planning',
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Zap,
        title: 'Automated Appointment Optimisation',
        description: 'Intelligent appointment optimization considering treatment complexity, patient preferences, and resource availability',
        metrics: 'Optimized practice efficiency',
        color: 'from-yellow-500 to-yellow-600'
      },
      {
        icon: Shield,
        title: 'Clinical Decision Support',
        description: 'Evidence-based clinical decision support with treatment guidelines, drug interactions, and best practice recommendations',
        metrics: 'Clinical excellence support',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Globe,
        title: 'Multi-Practice Management',
        description: 'Centralised management for multiple dental practices with unified reporting and resource sharing',
        metrics: 'Enterprise practice management',
        color: 'from-cyan-500 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Practice Analytics',
        description: 'Advanced predictive analytics for patient no-shows, treatment outcomes, and practice growth opportunities',
        metrics: 'Predictive practice insights',
        color: 'from-green-500 to-green-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Dental Practice Security',
        description: 'Comprehensive security framework for dental practices with patient data protection and HIPAA compliance',
        metrics: 'Dental-grade security',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Practice Operations',
        description: 'Instant practice management with real-time scheduling, billing, and patient communication capabilities',
        metrics: 'Real-time practice efficiency',
        color: 'from-yellow-500 to-yellow-600'
      },
      {
        icon: Settings,
        title: 'Scalable Dental Platform',
        description: 'Cloud-based dental practice platform supporting multiple locations with seamless data synchronisation',
        metrics: 'Multi-practice scalability',
        color: 'from-emerald-500 to-emerald-600'
      },
      {
        icon: Lock,
        title: 'Secure Imaging Management',
        description: 'Encrypted dental imaging storage with secure transmission and role-based access controls',
        metrics: 'Secure imaging protection',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Database,
        title: 'FHIR-Compliant Integration',
        description: 'Healthcare interoperability with FHIR-compliant data exchange and secure third-party integrations',
        metrics: 'Healthcare interoperability',
        color: 'from-indigo-500 to-indigo-600'
      },
      {
        icon: Activity,
        title: 'Practice Performance Monitoring',
        description: 'Comprehensive practice performance monitoring with system health analytics and automated optimization',
        metrics: '99.9% practice uptime',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: CheckCircle,
        title: 'Regulatory Compliance Suite',
        description: 'Automated compliance with dental practice regulations, audit trail management, and reporting',
        metrics: 'Complete regulatory compliance',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Multi-Tenant Architecture',
        description: 'Secure multi-tenant platform with practice-specific customisation and data isolation',
        metrics: 'Enterprise multi-tenancy',
        color: 'from-cyan-500 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Advanced Practice Analytics',
        description: 'Enterprise analytics platform with machine learning insights and predictive practice modelling',
        metrics: 'Advanced practice intelligence',
        color: 'from-blue-500 to-blue-600'
      }
    ]
  },

  'telemedicine-platform': {
    keyBenefits: [
      {
        icon: Heart,
        title: 'Secure Virtual Consultations',
        description: 'HIPAA-compliant video consultations with integrated patient records and prescription management',
        metrics: '99.9% consultation availability',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: TrendingUp,
        title: 'AI-Powered Triage System',
        description: 'Intelligent patient triage with symptom analysis and priority scheduling for optimal care delivery',
        metrics: '90% triage accuracy',
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Patient Engagement Platform',
        description: 'Comprehensive patient portal with appointment scheduling, health tracking, and educational resources',
        metrics: '95% patient satisfaction',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: Monitor,
        title: 'Remote Health Monitoring',
        description: 'Continuous remote patient monitoring with wearable device integration and automated health alerts',
        metrics: '24/7 patient monitoring',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Shield,
        title: 'Prescription Management System',
        description: 'Digital prescription management with e-prescribing, medication tracking, and pharmacy integration',
        metrics: '100% prescription accuracy',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: CheckCircle,
        title: 'Multi-Provider Coordination',
        description: 'Seamless care coordination between multiple healthcare providers with shared patient records',
        metrics: 'Unified care coordination',
        color: 'from-cyan-500 to-cyan-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Brain,
        title: 'Clinical Decision Support',
        description: 'AI-assisted diagnosis support with evidence-based recommendations and drug interaction checking',
        metrics: 'Clinical intelligence',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Activity,
        title: 'Remote Patient Monitoring',
        description: 'Continuous patient monitoring with wearable device integration and automated health alerts',
        metrics: 'Continuous care monitoring',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Multi-Provider Network',
        description: 'Integrated healthcare network with specialist referrals and collaborative care coordination',
        metrics: 'Seamless care coordination',
        color: 'from-indigo-500 to-indigo-600'
      },
      {
        icon: Database,
        title: 'EHR Integration Hub',
        description: 'Comprehensive electronic health record integration with real-time data synchronisation across platforms',
        metrics: 'Complete EHR connectivity',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: Target,
        title: 'Predictive Health Analytics',
        description: 'Machine learning algorithms predicting health risks and recommending preventive care interventions',
        metrics: 'Predictive healthcare insights',
        color: 'from-cyan-500 to-cyan-600'
      },
      {
        icon: Zap,
        title: 'Emergency Response System',
        description: 'Automated emergency response with critical alerts, emergency contacts, and rapid care escalation',
        metrics: 'Emergency care coordination',
        color: 'from-red-500 to-red-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Medical Data Protection',
        description: 'End-to-end encryption with secure patient data storage and access controls meeting healthcare standards',
        metrics: 'Medical-grade security',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Healthcare Delivery',
        description: 'Instant consultation capabilities with low-latency video and immediate health data synchronisation',
        metrics: 'Real-time care delivery',
        color: 'from-yellow-500 to-yellow-600'
      },
      {
        icon: Settings,
        title: 'Scalable Telehealth Infrastructure',
        description: 'Cloud-native platform supporting unlimited patients and providers with global accessibility',
        metrics: 'Unlimited patient capacity',
        color: 'from-emerald-500 to-emerald-600'
      }
    ]
  },

  'health-data-analytics': {
    keyBenefits: [
      {
        icon: Activity,
        title: 'Population Health Intelligence',
        description: 'Advanced population health analytics with disease pattern identification and health outcome prediction',
        metrics: '360° population insights',
        color: 'from-teal-500 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Health Modelling',
        description: 'AI-powered predictive models identifying health risks and optimising preventive care strategies',
        metrics: '85% risk prediction accuracy',
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Clinical Outcome Analytics',
        description: 'Comprehensive clinical analytics with treatment effectiveness measurement and care quality assessment',
        metrics: 'Evidence-based insights',
        color: 'from-purple-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Health Data Integration Hub',
        description: 'Unified health data platform integrating EMRs, lab results, imaging, and patient-generated data',
        metrics: 'Complete data integration',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: Brain,
        title: 'Clinical Research Analytics',
        description: 'Advanced research analytics with clinical trial support and real-world evidence generation',
        metrics: 'Research-grade analytics',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: Users,
        title: 'Patient Journey Analytics',
        description: 'Comprehensive patient journey mapping with care pathway optimisation and outcome tracking',
        metrics: 'Complete patient insights',
        color: 'from-indigo-500 to-indigo-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Secure Health Analytics',
        description: 'Privacy-preserving analytics with de-identification and secure multi-party computation capabilities',
        metrics: 'Privacy-protected insights',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Health Intelligence',
        description: 'Instant health data processing with real-time dashboards and automated alert generation',
        metrics: 'Live health insights',
        color: 'from-yellow-500 to-yellow-600'
      },
      {
        icon: Settings,
        title: 'Scalable Analytics Platform',
        description: 'Enterprise healthcare analytics infrastructure supporting unlimited data volumes and users',
        metrics: 'Unlimited analytics scale',
        color: 'from-emerald-500 to-emerald-600'
      }
    ]
  },

  'clinical-trial-management': {
    keyBenefits: [
      {
        icon: Stethoscope,
        title: 'Intelligent Trial Design',
        description: 'AI-powered trial design optimisation with patient stratification and endpoint selection for maximum efficacy',
        metrics: '40% faster trial design',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Predictive Patient Recruitment',
        description: 'Advanced patient matching algorithms with recruitment optimisation and retention prediction models',
        metrics: '60% recruitment improvement',
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Real-Time Trial Monitoring',
        description: 'Continuous trial oversight with automated data quality checks and safety signal detection',
        metrics: '99% data quality',
        color: 'from-green-500 to-green-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Electronic Data Capture',
        description: 'Advanced EDC system with mobile data collection, offline capability, and intelligent data validation',
        metrics: 'Seamless data capture',
        color: 'from-purple-500 to-purple-600'
      },
      {
        icon: Shield,
        title: 'Regulatory Compliance Suite',
        description: 'Comprehensive regulatory compliance with GCP, FDA 21 CFR Part 11, and international trial standards',
        metrics: 'Global regulatory compliance',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: Globe,
        title: 'Multi-Site Trial Coordination',
        description: 'Centralised multi-site trial management with site performance analytics and communication tools',
        metrics: 'Global trial coordination',
        color: 'from-cyan-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Clinical Data Security',
        description: 'Secure clinical data protection with audit trails, electronic signatures, and tamper-proof records',
        metrics: 'Clinical-grade security',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Trial Intelligence',
        description: 'Instant trial monitoring with real-time analytics and automated safety reporting capabilities',
        metrics: 'Live trial insights',
        color: 'from-yellow-500 to-yellow-600'
      },
      {
        icon: Settings,
        title: 'Scalable Trial Platform',
        description: 'Enterprise clinical trial infrastructure supporting unlimited studies and global research networks',
        metrics: 'Unlimited trial scale',
        color: 'from-emerald-500 to-emerald-600'
      }
    ]
  },

  'pharmacy-management-system': {
    keyBenefits: [
      {
        icon: Pill,
        title: 'Intelligent Inventory Management',
        description: 'AI-powered pharmaceutical inventory with expiry tracking, automated reordering, and waste minimisation',
        metrics: '95% inventory accuracy',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: TrendingUp,
        title: 'Prescription Analytics',
        description: 'Advanced prescription analytics with drug interaction checking and therapeutic substitution recommendations',
        metrics: '99% prescription accuracy',
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Patient Medication Management',
        description: 'Comprehensive patient medication tracking with adherence monitoring and automated refill reminders',
        metrics: '85% adherence improvement',
        color: 'from-purple-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Shield,
        title: 'Clinical Decision Support',
        description: 'Advanced clinical decision support with drug-drug interactions, allergy checking, and dosing guidance',
        metrics: 'Clinical safety assurance',
        color: 'from-orange-500 to-orange-600'
      },
      {
        icon: Target,
        title: 'Automated Dispensing Control',
        description: 'Robotic dispensing integration with barcode verification and automated packaging systems',
        metrics: 'Automated dispensing accuracy',
        color: 'from-indigo-500 to-indigo-600'
      },
      {
        icon: Globe,
        title: 'Regulatory Compliance Management',
        description: 'Comprehensive pharmaceutical compliance with controlled substance tracking and audit reporting',
        metrics: '100% regulatory compliance',
        color: 'from-cyan-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Pharmaceutical Data Security',
        description: 'Secure prescription and patient data protection with encrypted storage and access controls',
        metrics: 'Pharmacy-grade security',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Prescription Processing',
        description: 'Instant prescription verification and processing with real-time insurance claim adjudication',
        metrics: 'Real-time processing',
        color: 'from-yellow-500 to-yellow-600'
      },
      {
        icon: Settings,
        title: 'Scalable Pharmacy Platform',
        description: 'Multi-location pharmacy management with centralised inventory and distributed dispensing capabilities',
        metrics: 'Multi-pharmacy scale',
        color: 'from-emerald-500 to-emerald-600'
      }
    ]
  },

  'healthcare-compliance-platform': {
    keyBenefits: [
      {
        icon: Shield,
        title: 'Comprehensive Compliance Monitoring',
        description: 'Automated healthcare compliance monitoring with HIPAA, GDPR, and regulatory requirement tracking',
        metrics: '100% compliance coverage',
        color: 'from-red-500 to-orange-600'
      },
      {
        icon: TrendingUp,
        title: 'Risk Assessment Analytics',
        description: 'Advanced risk analytics with compliance gap identification and remediation prioritisation',
        metrics: 'Proactive risk management',
        color: 'from-blue-500 to-blue-600'
      },
      {
        icon: Target,
        title: 'Automated Audit Preparation',
        description: 'Intelligent audit trail generation with automated documentation and compliance evidence collection',
        metrics: 'Audit-ready documentation',
        color: 'from-purple-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Database,
        title: 'Policy Management Engine',
        description: 'Comprehensive policy and procedure management with version control and staff acknowledgment tracking',
        metrics: 'Complete policy control',
        color: 'from-green-500 to-green-600'
      },
      {
        icon: Users,
        title: 'Training & Certification Tracking',
        description: 'Automated staff training management with certification tracking and competency assessment',
        metrics: 'Staff compliance assurance',
        color: 'from-indigo-500 to-indigo-600'
      },
      {
        icon: Globe,
        title: 'Multi-Jurisdictional Compliance',
        description: 'Global healthcare compliance management with jurisdiction-specific requirements and updates',
        metrics: 'Global compliance ready',
        color: 'from-cyan-500 to-cyan-600'
      }
    ],
    securityPerformance: [
      {
        icon: Shield,
        title: 'Compliance Data Protection',
        description: 'Secure compliance data management with encrypted storage and role-based access controls',
        metrics: 'Compliance-grade security',
        color: 'from-red-500 to-red-600'
      },
      {
        icon: Zap,
        title: 'Real-Time Compliance Monitoring',
        description: 'Continuous compliance monitoring with instant alerts for violations and corrective action tracking',
        metrics: 'Live compliance status',
        color: 'from-yellow-500 to-yellow-600'
      },
      {
        icon: Settings,
        title: 'Scalable Compliance Platform',
        description: 'Enterprise compliance infrastructure supporting unlimited facilities and regulatory frameworks',
        metrics: 'Unlimited compliance scale',
        color: 'from-emerald-500 to-emerald-600'
      }
    ]
  }
};